import React, { useState, useEffect } from 'react';
import { Plus, Wrench, Clock, CheckCircle, AlertCircle, Edit, Trash2, UserCheck, CheckSquare, XCircle } from 'lucide-react';
import api from '../services/api';
import MaintenanceForm from '../components/Forms/MaintenanceForm';
import Modal from '../components/Common/Modal';
import LoadingSpinner from '../components/Common/LoadingSpinner';

const Maintenance = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [assetUnits, setAssetUnits] = useState([]);
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState(null);
    const [assignTo, setAssignTo] = useState('');
    const [resolution, setResolution] = useState('');
    const [actualCost, setActualCost] = useState('');

    useEffect(() => {
        fetchTickets();
        fetchAssetUnits();
        fetchStats();
        fetchUsers();
    }, []);

    const fetchTickets = async () => {
        try {
            const response = await api.get('/maintenance');
            setTickets(response.data.data.data || []);
        } catch (error) {
            console.error('Error fetching tickets:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAssetUnits = async () => {
        try {
            const response = await api.get('/assets');
            const assets = response.data.data.data;
            const units = [];
            for (const asset of assets) {
                const unitsResponse = await api.get(`/assets/${asset.id}/units`);
                units.push(...unitsResponse.data.data.map(u => ({ ...u, asset_name: asset.name })));
            }
            setAssetUnits(units);
        } catch (error) {
            console.error('Error fetching asset units:', error);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await api.get('/maintenance/stats');
            setStats(response.data.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await api.get('/tenants/users?role=caretaker');
            setUsers(response.data.data.data || []);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const handleSubmitTicket = async (data) => {
        try {
            await api.post('/maintenance', data);
            setShowModal(false);
            fetchTickets();
            fetchStats();
        } catch (error) {
            console.error('Error submitting ticket:', error);
            alert('Failed to submit ticket');
        }
    };

    const handleEditTicket = async (data) => {
        try {
            await api.put(`/maintenance/${selectedTicket.id}`, data);
            setShowEditModal(false);
            setSelectedTicket(null);
            fetchTickets();
            fetchStats();
        } catch (error) {
            console.error('Error updating ticket:', error);
            alert('Failed to update ticket');
        }
    };

    const handleDeleteTicket = async () => {
        try {
            await api.delete(`/maintenance/${selectedTicket.id}`);
            setShowDeleteModal(false);
            setSelectedTicket(null);
            fetchTickets();
            fetchStats();
        } catch (error) {
            console.error('Error deleting ticket:', error);
            alert('Failed to delete ticket');
        }
    };

    const handleAssignTicket = async () => {
        if (!assignTo) {
            alert('Please select someone to assign');
            return;
        }
        try {
            await api.post(`/maintenance/${selectedTicket.id}/assign`, { assigned_to: assignTo });
            setShowAssignModal(false);
            setSelectedTicket(null);
            setAssignTo('');
            fetchTickets();
            fetchStats();
        } catch (error) {
            console.error('Error assigning ticket:', error);
            alert('Failed to assign ticket');
        }
    };

    const handleResolveTicket = async () => {
        try {
            await api.post(`/maintenance/${selectedTicket.id}/resolve`, { 
                resolution, 
                actual_cost: actualCost || null 
            });
            setShowAssignModal(false);
            setSelectedTicket(null);
            setResolution('');
            setActualCost('');
            fetchTickets();
            fetchStats();
        } catch (error) {
            console.error('Error resolving ticket:', error);
            alert('Failed to resolve ticket');
        }
    };

    const getPriorityBadge = (priority) => {
        const colors = {
            low: 'bg-gray-100 text-gray-800',
            medium: 'bg-blue-100 text-blue-800',
            high: 'bg-orange-100 text-orange-800',
            emergency: 'bg-red-100 text-red-800'
        };
        return colors[priority] || colors.medium;
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'open': return <AlertCircle className="h-5 w-5 text-yellow-500" />;
            case 'in_progress': return <Clock className="h-5 w-5 text-blue-500" />;
            case 'resolved': return <CheckCircle className="h-5 w-5 text-green-500" />;
            case 'cancelled': return <XCircle className="h-5 w-5 text-red-500" />;
            default: return <Wrench className="h-5 w-5 text-gray-500" />;
        }
    };

    const getStatusBadge = (status) => {
        const colors = {
            open: 'bg-yellow-100 text-yellow-800',
            in_progress: 'bg-blue-100 text-blue-800',
            resolved: 'bg-green-100 text-green-800',
            cancelled: 'bg-red-100 text-red-800'
        };
        return colors[status] || colors.open;
    };

    if (loading) return <LoadingSpinner size="lg" />;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Maintenance</h1>
                    <p className="text-gray-600">Track and manage maintenance requests</p>
                </div>
                <button
                    onClick={() => setShowModal(true)}
                    className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                    <Plus className="h-5 w-5 mr-2" />
                    Report Issue
                </button>
            </div>
            
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div className="bg-white rounded-lg shadow p-4">
                        <p className="text-sm text-gray-600">Open</p>
                        <p className="text-2xl font-bold text-yellow-600">{stats.open_tickets || 0}</p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <p className="text-sm text-gray-600">In Progress</p>
                        <p className="text-2xl font-bold text-blue-600">{stats.in_progress_tickets || 0}</p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <p className="text-sm text-gray-600">Resolved</p>
                        <p className="text-2xl font-bold text-green-600">{stats.resolved_tickets || 0}</p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <p className="text-sm text-gray-600">Emergency</p>
                        <p className="text-2xl font-bold text-red-600">{stats.emergency_tickets || 0}</p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <p className="text-sm text-gray-600">Avg Resolution</p>
                        <p className="text-2xl font-bold text-purple-600">
                            {stats.avg_resolution_hours ? (stats.avg_resolution_hours / 24).toFixed(1) : 0} days
                        </p>
                    </div>
                </div>
            )}
            
            <div className="space-y-4">
                {tickets.map((ticket) => (
                    <div key={ticket.id} className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3 flex-1">
                                {getStatusIcon(ticket.status)}
                                <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-gray-900">{ticket.title}</h3>
                                    <p className="text-sm text-gray-600 mt-1">{ticket.description}</p>
                                    <div className="flex items-center space-x-4 mt-2">
                                        <span className="text-xs text-gray-500">
                                            Asset: {ticket.asset_name} {ticket.unit_number ? `- Unit ${ticket.unit_number}` : ''}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                            Reported by: {ticket.reporter_first_name} {ticket.reporter_last_name}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                            {new Date(ticket.created_at).toLocaleDateString()}
                                        </span>
                                        {ticket.completed_date && (
                                            <span className="text-xs text-green-600">
                                                Completed: {new Date(ticket.completed_date).toLocaleDateString()}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <span className={`px-2 py-1 text-xs rounded-full ${getPriorityBadge(ticket.priority)}`}>
                                    {ticket.priority}
                                </span>
                                <span className={`px-2 py-1 text-xs rounded-full capitalize ${getStatusBadge(ticket.status)}`}>
                                    {ticket.status.replace('_', ' ')}
                                </span>
                            </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="mt-4 pt-4 border-t flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                                {ticket.assigned_first_name && (
                                    <span className="text-sm text-gray-600 flex items-center">
                                        <UserCheck className="h-4 w-4 mr-1" />
                                        Assigned to: {ticket.assigned_first_name} {ticket.assigned_last_name}
                                    </span>
                                )}
                                {ticket.actual_cost && (
                                    <span className="text-sm text-gray-600">
                                        Cost: KES {parseFloat(ticket.actual_cost).toLocaleString()}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center space-x-2">
                                {ticket.status === 'open' && (
                                    <button
                                        onClick={() => {
                                            setSelectedTicket(ticket);
                                            setShowAssignModal(true);
                                        }}
                                        className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                                    >
                                        <UserCheck className="h-4 w-4 mr-1" />
                                        Assign
                                    </button>
                                )}
                                {ticket.status === 'in_progress' && (
                                    <button
                                        onClick={() => {
                                            setSelectedTicket(ticket);
                                            setShowAssignModal(true);
                                            setResolution('');
                                            setActualCost('');
                                        }}
                                        className="text-green-600 hover:text-green-800 text-sm flex items-center"
                                    >
                                        <CheckSquare className="h-4 w-4 mr-1" />
                                        Resolve
                                    </button>
                                )}
                                <button
                                    onClick={() => {
                                        setSelectedTicket(ticket);
                                        setShowEditModal(true);
                                    }}
                                    className="text-indigo-600 hover:text-indigo-800"
                                >
                                    <Edit className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => {
                                        setSelectedTicket(ticket);
                                        setShowDeleteModal(true);
                                    }}
                                    className="text-red-600 hover:text-red-800"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            
            {/* Create Ticket Modal */}
            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title="Report Maintenance Issue"
                size="lg"
            >
                <MaintenanceForm
                    onSubmit={handleSubmitTicket}
                    isLoading={false}
                    assetUnits={assetUnits}
                />
            </Modal>
            
            {/* Edit Ticket Modal */}
            <Modal
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setSelectedTicket(null);
                }}
                title="Edit Maintenance Ticket"
                size="lg"
            >
                {selectedTicket && (
                    <MaintenanceForm
                        onSubmit={handleEditTicket}
                        isLoading={false}
                        assetUnits={assetUnits}
                        initialData={selectedTicket}
                        isEdit={true}
                    />
                )}
            </Modal>
            
            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setSelectedTicket(null);
                }}
                title="Delete Maintenance Ticket"
                size="sm"
            >
                <div className="space-y-4">
                    <p className="text-gray-700">
                        Are you sure you want to delete ticket <strong>#{selectedTicket?.id}</strong>?
                    </p>
                    <p className="text-sm text-red-600">
                        This action cannot be undone.
                    </p>
                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            onClick={() => {
                                setShowDeleteModal(false);
                                setSelectedTicket(null);
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleDeleteTicket}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                            Delete
                        </button>
                    </div>
                </div>
            </Modal>
            
            {/* Assign Ticket Modal */}
            <Modal
                isOpen={showAssignModal}
                onClose={() => {
                    setShowAssignModal(false);
                    setSelectedTicket(null);
                    setAssignTo('');
                    setResolution('');
                    setActualCost('');
                }}
                title={selectedTicket?.status === 'in_progress' ? "Resolve Ticket" : "Assign Ticket"}
                size="md"
            >
                {selectedTicket?.status === 'in_progress' ? (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Resolution Notes
                            </label>
                            <textarea
                                value={resolution}
                                onChange={(e) => setResolution(e.target.value)}
                                rows={3}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                                placeholder="Describe how the issue was resolved..."
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Actual Cost (KES)
                            </label>
                            <input
                                type="number"
                                value={actualCost}
                                onChange={(e) => setActualCost(e.target.value)}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                                placeholder="0.00"
                            />
                        </div>
                        <div className="flex justify-end space-x-3 pt-4">
                            <button
                                onClick={() => {
                                    setShowAssignModal(false);
                                    setSelectedTicket(null);
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleResolveTicket}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                                Mark as Resolved
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Assign To
                            </label>
                            <select
                                value={assignTo}
                                onChange={(e) => setAssignTo(e.target.value)}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                                required
                            >
                                <option value="">Select a caretaker...</option>
                                {users.map(user => (
                                    <option key={user.id} value={user.id}>
                                        {user.first_name} {user.last_name} ({user.email})
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex justify-end space-x-3 pt-4">
                            <button
                                onClick={() => {
                                    setShowAssignModal(false);
                                    setSelectedTicket(null);
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAssignTicket}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Assign Ticket
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default Maintenance;